import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Pagination,
  Stack,
  Paper,
  Breadcrumbs,
  Link,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Tooltip,
  Switch,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useNavigate } from "react-router-dom";
import {
  getCategories,
  deleteCategory,
  getProductsByCategory,
  updateCategory,
  toggleCategoryActive,
  toSlug,
} from "../../utils/storage";

const PAGE_SIZE = 10;

const Category = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchValue, setSearchValue] = useState(null);
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  // Edit state
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", slug: "", eyebrow: "", description: "" });
  const [editSlugManual, setEditSlugManual] = useState(false);
  const [editErrors, setEditErrors] = useState({});

  const load = () => {
    const all = getCategories();
    setCategories(all);
    setFiltered(all);
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!searchValue) setFiltered(categories);
    else setFiltered(categories.filter((c) => c.id === searchValue.id));
    setPage(1);
  }, [searchValue, categories]);

  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);

  // ── Delete ──
  const handleDelete = () => {
    if (!deleteTarget) return;
    const productCount = getProductsByCategory(deleteTarget.id).length;
    deleteCategory(deleteTarget.id);
    setDeleteTarget(null);
    load();
    setSnack({
      open: true,
      message: `"${deleteTarget.name}" deleted${productCount > 0 ? ` along with ${productCount} product(s)` : ""}.`,
      severity: "success",
    });
  };

  // ── Toggle Active ──
  const handleToggleActive = (cat) => {
    toggleCategoryActive(cat.id);
    load();
    setSnack({
      open: true,
      message: `"${cat.name}" ${cat.isActive !== false ? "deactivated" : "activated"}.`,
      severity: "info",
    });
  };

  // ── Edit ──
  const openEdit = (cat) => {
    setEditTarget(cat);
    setEditForm({
      name: cat.name,
      slug: cat.slug,
      eyebrow: cat.eyebrow,
      description: cat.description,
    });
    setEditSlugManual(false);
    setEditErrors({});
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "name" && !editSlugManual) next.slug = toSlug(value);
      return next;
    });
    setEditErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleEditSlugChange = (e) => {
    setEditSlugManual(true);
    setEditForm((prev) => ({ ...prev, slug: e.target.value }));
    setEditErrors((prev) => ({ ...prev, slug: "" }));
  };

  const validateEdit = () => {
    const temp = {};
    if (!editForm.name.trim()) temp.name = "Category name is required";
    else if (editForm.name.trim().length < 3) temp.name = "Minimum 3 characters";
    if (!editForm.slug.trim()) temp.slug = "Slug is required";
    else if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(editForm.slug.trim()))
      temp.slug = "Lowercase letters, numbers and hyphens only";
    if (!editForm.eyebrow.trim()) temp.eyebrow = "Eyebrow label is required";
    if (!editForm.description.trim()) temp.description = "Description is required";
    else if (editForm.description.trim().length < 10) temp.description = "Minimum 10 characters";
    setEditErrors(temp);
    return Object.keys(temp).length === 0;
  };

  const handleEditSave = () => {
    if (!validateEdit()) return;
    updateCategory(editTarget.id, {
      name: editForm.name.trim(),
      slug: editForm.slug.trim(),
      eyebrow: editForm.eyebrow.trim(),
      description: editForm.description.trim(),
    });
    setEditTarget(null);
    load();
    setSnack({ open: true, message: `"${editForm.name.trim()}" updated successfully.`, severity: "success" });
  };

  return (
    <Box sx={{ p: 3, backgroundColor: "#f5f7fb", minHeight: "100vh" }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h5" fontWeight="600" color="#333">Category List</Typography>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
          <Link underline="hover" color="inherit" href="/admin/dashboard">Dashboard</Link>
          <Typography color="text.primary">Category List</Typography>
        </Breadcrumbs>
      </Box>

      {/* Main Card */}
      <Paper elevation={2} sx={{ borderRadius: 3, p: 4, backgroundColor: "#fff", minHeight: "500px" }}>
        {/* Card Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4, flexWrap: "wrap", gap: 2 }}>
          <Typography variant="h6" fontWeight="600">
            All Categories{" "}
            <Chip label={categories.length} size="small" color="primary" sx={{ ml: 1, fontWeight: 700 }} />
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Autocomplete
              sx={{ width: 250 }}
              options={categories}
              getOptionLabel={(o) => o?.name || ""}
              value={searchValue}
              onChange={(_, v) => setSearchValue(v)}
              renderInput={(params) => <TextField {...params} label="Search Category" size="small" />}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/admin/addCategory")}
              sx={{ borderRadius: 2, px: 2, py: 1, textTransform: "uppercase", fontWeight: "500", boxShadow: "none" }}
            >
              Add Category
            </Button>
          </Box>
        </Box>

        {/* Table */}
        {filtered.length === 0 ? (
          <Box sx={{ height: "250px", display: "flex", justifyContent: "center", alignItems: "center", borderRadius: 2, border: "1px dashed #dcdcdc", backgroundColor: "#fafafa" }}>
            <Typography variant="h6" color="text.secondary">No categories found.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#1976d2" }}>
                  {["#", "Name", "Slug", "Eyebrow", "Products", "Status", "Created", "Actions"].map((h) => (
                    <TableCell key={h} sx={{ color: "#fff", fontWeight: "bold", whiteSpace: "nowrap" }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginated.map((cat, idx) => {
                  const productCount = getProductsByCategory(cat.id).length;
                  const isActive = cat.isActive !== false;
                  return (
                    <TableRow key={cat.id} hover sx={{ opacity: isActive ? 1 : 0.55 }}>
                      <TableCell sx={{ color: "#777", fontSize: "13px" }}>{(page - 1) * PAGE_SIZE + idx + 1}</TableCell>
                      <TableCell fontWeight={600}>{cat.name}</TableCell>
                      <TableCell>
                        <Chip label={`/${cat.slug}`} size="small" variant="outlined" sx={{ fontSize: "11px", fontFamily: "monospace" }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: "13px", color: "#555" }}>{cat.eyebrow}</TableCell>
                      <TableCell>
                        <Chip label={productCount} size="small" color={productCount > 0 ? "success" : "default"} />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={isActive ? "Active" : "Inactive"}
                          size="small"
                          color={isActive ? "success" : "default"}
                          variant={isActive ? "filled" : "outlined"}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: "12px", color: "#888" }}>
                        {new Date(cat.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          {/* Edit */}
                          <Tooltip title="Edit Category">
                            <IconButton size="small" color="primary" onClick={() => openEdit(cat)}>
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {/* Deactivate toggle */}
                          <Tooltip title={isActive ? "Deactivate (hide from header & frontend)" : "Activate"}>
                            <Switch
                              size="small"
                              checked={isActive}
                              onChange={() => handleToggleActive(cat)}
                              color="success"
                            />
                          </Tooltip>
                          {/* Delete */}
                          <Tooltip title="Delete Category">
                            <IconButton size="small" color="error" onClick={() => setDeleteTarget(cat)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination */}
        <Box sx={{ mt: 4, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {filtered.length} result{filtered.length !== 1 ? "s" : ""}
          </Typography>
          {totalPages > 1 && (
            <Stack spacing={2}>
              <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} color="primary" shape="circular" />
            </Stack>
          )}
        </Box>
      </Paper>

      {/* ── Edit Dialog ── */}
      <Dialog open={!!editTarget} onClose={() => setEditTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle fontWeight={700}>Edit Category</DialogTitle>
        <DialogContent sx={{ pt: "16px !important" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 0.5 }}>
            {/* Name + Slug */}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <Box sx={{ flex: 1, minWidth: "200px" }}>
                <Typography variant="subtitle2" fontWeight={600} mb={0.8}>Category Name *</Typography>
                <TextField
                  fullWidth size="small" name="name"
                  value={editForm.name} onChange={handleEditChange}
                  error={!!editErrors.name} helperText={editErrors.name}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: "200px" }}>
                <Typography variant="subtitle2" fontWeight={600} mb={0.8}>URL Slug *</Typography>
                <TextField
                  fullWidth size="small" name="slug"
                  value={editForm.slug} onChange={handleEditSlugChange}
                  error={!!editErrors.slug}
                  helperText={editErrors.slug || `/${editForm.slug || "your-slug"}`}
                />
              </Box>
            </Box>
            {/* Eyebrow */}
            <Box>
              <Typography variant="subtitle2" fontWeight={600} mb={0.8}>Eyebrow Label *</Typography>
              <TextField
                fullWidth size="small" name="eyebrow"
                value={editForm.eyebrow} onChange={handleEditChange}
                error={!!editErrors.eyebrow} helperText={editErrors.eyebrow}
              />
            </Box>
            {/* Description */}
            <Box>
              <Typography variant="subtitle2" fontWeight={600} mb={0.8}>Description *</Typography>
              <TextField
                fullWidth multiline rows={3} name="description"
                value={editForm.description} onChange={handleEditChange}
                error={!!editErrors.description} helperText={editErrors.description}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button onClick={() => setEditTarget(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave}>Save Changes</Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Dialog ── */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight={700}>Delete Category?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>"{deleteTarget?.name}"</strong>?
            <br />
            <Typography component="span" color="error" variant="body2">
              All products in this category will also be deleted.
            </Typography>
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} variant="filled">{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Category;